import React, { useEffect, useRef, useState } from "react";

import { IChannel } from "@formant/core/model/IChannel";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import {
    Avatar,
    AvatarFallback,
    Button,
    Check,
    ChevronDown,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Grid2X2,
    Input,
    Search,
    Settings,
    Sidebar,
    SidebarFooter,
    SidebarHeader,
    SidebarProvider,
} from "@formant/ui-kit";

import { track } from "../../analytics";
import {
    appStore,
    authStore,
    channelStore,
    organizationStore,
    routerStore,
    userStore,
} from "../../stores";
import { formatUserDisplayName } from "../../utils/formatUserDisplayName";
import { FleetHeader } from "./fleet-header";
import { FleetSidebarContent } from "./fleet-sidebar-content";
import { ITagsAndGroups } from "./fleet-sidebar.types";

export interface INewSidebarProps {
    handleSideBarClose: () => void;
    tagsAndGroups: ITagsAndGroups[];
    devicesData: IDeviceDetails[];
}

const defaultSelectedOption = "All Categories";

export const NewSidebar = (props: INewSidebarProps) => {
    const defaultCategoryIsSet = useRef(false);
    const [selectedOption, setSelectedOption] = useState<string>(
        defaultSelectedOption,
    );
    const [channels, setChannels] = useState<IChannel[]>([]);
    const filterOptionsRef = useRef({
        main: [] as { id: string; label: string }[],
        other: [] as { id: string; label: string }[],
    });
    const user = userStore.authenticated();
    const organization = organizationStore.authenticated();
    const { routeTo, navigate } = routerStore;

    const onLogout = () => {
        track("Last online");
        authStore.logout();
        window.location.href = "/";
    };

    const isKiosk = authStore.hasAccess("kiosk");
    const hideSettings = false;
    const hideChannels = false;
    const hideApplications = true;

    useEffect(() => {
        if (
            organization &&
            organization.defaultGroupCategory &&
            selectedOption === defaultSelectedOption &&
            !defaultCategoryIsSet.current
        ) {
            setSelectedOption(organization.defaultGroupCategory);
            defaultCategoryIsSet.current = true;
        }
    }, [organization, selectedOption]);

    useEffect(() => {
        if (!isKiosk && !hideChannels) {
            channelStore
                .listAsync()
                .then(data => {
                    if (data.length) {
                        filterOptionsRef.current.other.push({
                            id: "Channels",
                            label: "Channels",
                        });
                        setChannels(data);
                    }
                })
                .catch(console.error);
        }
    }, []);

    filterOptionsRef.current.main = props.tagsAndGroups.map(_ => ({
        id: _.tag.tagKey,
        label: _.tag.tagKey,
    }));

    filterOptionsRef.current.main.unshift({
        id: defaultSelectedOption,
        label: defaultSelectedOption,
    });

    return (
        <SidebarProvider>
            <Sidebar collapsible="offcanvas" className="border-r-0">
                <div className="bg-formant-color-background-secondary">
                    <FleetHeader
                        handleSideBarClose={props.handleSideBarClose}
                    />
                    <SidebarHeader className=" px-4 ">
                        <div className="flex space-x-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-2 top-[0.7rem] h-4 w-4 text-formant-color-text-secondary" />
                                <Input
                                    placeholder="Search"
                                    className="border-none pl-8 text-formant-color-text-secondary cursor-pointer placeholder:text-formant-color-text-secondary"
                                    onClick={() => {
                                        appStore.jump = !appStore.jump;
                                    }}
                                />
                            </div>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild={true}>
                                    <Button className="bg-formant-color-action-tertiary text-formant-color-text-secondary hover:bg-formant-color-text-tertiary l-02 h-8 hover:shadow-none rounded-full text-[0.8rem] py-0">
                                        {selectedOption}
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-56 bg-formant-color-background-surface border border-formant-color-border-active overflow-auto mobile-scroll-container">
                                    <DropdownMenuLabel className="text-sm font-normal  text-formant-color-text-secondary  pl-8 font-bold">
                                        VIEW DEVICES BY
                                    </DropdownMenuLabel>
                                    <DropdownMenuGroup>
                                        {filterOptionsRef.current.main.map(
                                            option => (
                                                <DropdownMenuItem
                                                    key={option.id}
                                                    className="flex capitalize items-center cursor-pointer gap-2  text-formant-color-text-secondary "
                                                    onSelect={() =>
                                                        setSelectedOption(
                                                            option.label,
                                                        )
                                                    }
                                                >
                                                    {option.label && (
                                                        <Check
                                                            className={`h-4 w-4 transition-opacity ${
                                                                selectedOption ===
                                                                option.label
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            }`}
                                                        />
                                                    )}
                                                    <span className="flex-grow capitalize">
                                                        {option.label}
                                                    </span>
                                                </DropdownMenuItem>
                                            ),
                                        )}
                                    </DropdownMenuGroup>
                                    {filterOptionsRef.current.other.length >
                                        0 && (
                                        <>
                                            <DropdownMenuSeparator className="bg-[#282f45]" />
                                            <DropdownMenuLabel className="text-sm font-normal  text-formant-color-text-secondary  pl-8 font-bold">
                                                OTHER
                                            </DropdownMenuLabel>
                                            <DropdownMenuGroup>
                                                {filterOptionsRef.current.other.map(
                                                    option => (
                                                        <DropdownMenuItem
                                                            key={option.id}
                                                            className="flex capitalize items-center cursor-pointer gap-2  text-formant-color-text-secondary"
                                                            onSelect={() =>
                                                                setSelectedOption(
                                                                    option.label,
                                                                )
                                                            }
                                                        >
                                                            {option.label && (
                                                                <Check
                                                                    className={`h-4 w-4 transition-opacity ${
                                                                        selectedOption ===
                                                                        option.label
                                                                            ? "opacity-100"
                                                                            : "opacity-0"
                                                                    }`}
                                                                />
                                                            )}
                                                            <span className="flex-grow">
                                                                {option.label}
                                                            </span>
                                                        </DropdownMenuItem>
                                                    ),
                                                )}
                                            </DropdownMenuGroup>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </SidebarHeader>
                </div>

                <FleetSidebarContent
                    onClose={props.handleSideBarClose}
                    selectedOption={selectedOption}
                    tagsAndGroups={props.tagsAndGroups}
                    channels={channels}
                    devicesData={props.devicesData}
                />
                <SidebarFooter className="bg-formant-color-background-secondary">
                    <div className="grid grid-cols-2 gap-2">
                        {!hideSettings && (
                            <Button
                                data-testid="sidebar-button-settings"
                                variant="tertiary"
                                className="  min-h-12 hover:!shadow-none rounded-md  text-formant-color-text-primary   flex items-center gap-2"
                                onClick={() => {
                                    navigate(routeTo.settings()).catch(err =>
                                        console.error(err),
                                    );
                                    props.handleSideBarClose();
                                }}
                            >
                                <Settings className="h-4 w-4" />
                                Settings
                            </Button>
                        )}
                        {!hideApplications && (
                            <Button
                                data-testid="sidebar-button-applications"
                                variant="tertiary"
                                className="rounded-md min-h-12 hover:!shadow-none    text-formant-color-text-primary   flex items-center gap-2"
                                onClick={() => {
                                    navigate(routeTo.applications()).catch(
                                        err => console.error(err),
                                    );
                                    props.handleSideBarClose();
                                }}
                            >
                                <Grid2X2 className="h-4 w-4" />
                                Applications
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center justify-between rounded-lg">
                        {user && organization ? (
                            <div
                                className="flex items-center gap-3 p-3  cursor-pointer"
                                onClick={() => {
                                    navigate(routeTo.profile()).catch(err =>
                                        console.error(err),
                                    );
                                    props.handleSideBarClose();
                                }}
                            >
                                <Avatar>
                                    <AvatarFallback>
                                        {formatUserDisplayName(user)[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="font-medium text-formant-color-text-primary">
                                        {formatUserDisplayName(user)}
                                    </div>
                                    <div className="text-sm text-formant-color-text-secondary organization-name">
                                        {organization.name}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>No user data available</div>
                        )}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onLogout}
                            className=" min-h-8 text-formant-black rounded-full"
                        >
                            Sign out
                        </Button>
                    </div>
                </SidebarFooter>
            </Sidebar>
        </SidebarProvider>
    );
};
